import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';
import {
  SegmentedControl,
  SelectionCheckbox,
} from './MerchandisingWorkbenchUi';
import { useDispatch, useSelector } from 'react-redux';
import ConditionBuilder from './ConditionBuilder';
import ConditionFieldPicker from './ConditionFieldPicker';
import {
  appendCondition,
  decorateConditions,
  stripConditions,
} from '../model/conditionExpression';
import { humanField, preferredOperator } from '../model/conditionEditor';
import { defaultConditionValue } from '../model/conditionExpression';
import {
  searchProducts,
  resolveProducts,
} from '../../../slices/ProductSelectorSlice';
import { UnsavedChangesDialog } from './UnsavedChangesGuard';
import { previewMerchandisingSelectionSource } from '../state/merchandisingSlice';
import useSelectionSourcePreviews from './useSelectionSourcePreviews';
import SelectionSourcePreview, {
  SelectionSourceCount,
} from './SelectionSourcePreview';
import './SelectionSourcePreview.scss';

export const createSelectionSource = () => ({
  id: `source-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 7)}`,
  query: null,
  conditions: { match: 'all', items: [] },
});
const decoratePolicy = policy => ({
  ...policy,
  sources: policy.sources.map(source => ({
    ...source,
    conditions: decorateConditions(source.conditions),
  })),
});
const canonicalPolicy = draft => ({
  ...draft,
  sources: draft.sources.map(source => ({
    ...source,
    conditions: stripConditions(source.conditions, ['include_descendants']),
  })),
});
const conditionLeaves = group =>
  group.items.flatMap(item =>
    Array.isArray(item.items) ? conditionLeaves(item) : [item]
  );

export default function SelectionDialog({
  policy,
  support,
  languageId,
  open,
  onClose,
  onSave,
  valid,
}) {
  const t = useLocalizedMessage();
  const dispatch = useDispatch();
  const scopeKey = useSelector(
    state => state.merchandising?.scopeGeneration || 0
  );
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const productApi = useMemo(
    () => ({
      searchProducts: async (query, limit) => {
        const action = await dispatch(searchProducts({ query, limit }));
        if (searchProducts.fulfilled.match(action)) return action.payload;
        throw new Error(t('product.selector.search.error'));
      },
      resolveProducts: async ids => {
        const action = await dispatch(resolveProducts(ids));
        if (resolveProducts.fulfilled.match(action)) return action.payload;
        throw new Error(t('product.selector.search.error'));
      },
    }),
    [dispatch, t]
  );
  const conditionSchema = useMemo(
    () => Object.fromEntries(support.fields.map(field => [field.key, field])),
    [support.fields]
  );
  const [filterQuery, setFilterQuery] = useState('');

  const [draft, setDraft] = useState(() => decoratePolicy(policy));
  const [baseline, setBaseline] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [discard, setDiscard] = useState(false);
  const [conditionMenu, setConditionMenu] = useState(null);
  const pendingReveal = useRef(null);
  const [enteredGroup, setEnteredGroup] = useState(null);
  const content = useRef(null);
  useEffect(() => {
    if (open) {
      setDraft(decoratePolicy(policy));
      setBaseline(JSON.stringify(policy));
      setExpanded(policy.sources[0]?.id || null);
      setDiscard(false);
      setConditionMenu(null);
      pendingReveal.current = null;
      setEnteredGroup(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const sources = draft?.sources || [];
  const { previews, retry } = useSelectionSourcePreviews({
    sources,
    languageId,
    scopeKey,
    coordinatorKey: dispatch,
    enabled: open && support.selectionSourcePreview === true,
    requestPreview: async (payload, { signal }) => {
      const action = await dispatch(
        previewMerchandisingSelectionSource({
          ...payload,
          requestSignal: signal,
        })
      );
      if (previewMerchandisingSelectionSource.fulfilled.match(action))
        return action.payload;
      throw action.payload || action.error;
    },
  });
  useEffect(() => {
    const id = pendingReveal.current;
    if (
      !open ||
      !id ||
      id !== expanded ||
      id !== enteredGroup ||
      !content.current
    )
      return;
    const status = previews[id]?.status || 'validating';
    if (
      support.selectionSourcePreview &&
      ['validating', 'loading'].includes(status)
    )
      return;
    const element = Array.from(
      content.current.querySelectorAll('[data-selection-source]')
    ).find(node => node.dataset.selectionSource === id);
    pendingReveal.current = null;
    if (element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }, [open, expanded, enteredGroup, previews, support.selectionSourcePreview]);
  const cancelReveal = () => {
    pendingReveal.current = null;
  };
  const updateSource = (id, update) =>
    setDraft(current => ({
      ...current,
      sources: current.sources.map(source =>
        source.id === id ? update(source) : source
      ),
    }));
  const close = () => {
    cancelReveal();
    return JSON.stringify(canonicalPolicy(draft)) === baseline
      ? onClose()
      : setDiscard(true);
  };
  const labelFor = field => humanField(field.key, field);
  const fieldOptions = support.fields
    .map(descriptor => ({
      field: descriptor.key,
      descriptor,
      label: labelFor(descriptor),
    }))
    .filter(
      option =>
        !filterQuery.trim() ||
        `${option.label} ${option.field}`
          .toLocaleLowerCase()
          .includes(filterQuery.trim().toLocaleLowerCase())
    );
  const addGroup = () => {
    const source = {
      ...createSelectionSource(),
      conditions: decorateConditions(),
    };
    setDraft(current => ({
      ...current,
      sources: [...current.sources, source],
    }));
    pendingReveal.current = source.id;
    setEnteredGroup(null);
    setExpanded(source.id);
  };
  const draftValid = valid(canonicalPolicy(draft));
  return (
    <>
      <Dialog
        className="mp-dialog mp-selection-editor"
        open={open}
        onClose={close}
        maxWidth="md"
        fullWidth
        scroll="paper"
        onWheelCapture={cancelReveal}
        onTouchMoveCapture={cancelReveal}
        onPointerDownCapture={cancelReveal}
        onKeyDownCapture={cancelReveal}
        aria-labelledby="mp-selection-dialog-title"
      >
        <DialogTitle id="mp-selection-dialog-title">
          <span>
            {t('merchandising.advancedPrototype.selectionDialogTitle')}
          </span>
          <IconButton
            aria-label={t('merchandising.advancedPrototype.close')}
            onClick={close}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent ref={content}>
          <p className="mp-dialog-lead">
            {t('merchandising.advanced.combineGroups')}
          </p>
          <div className="mp-selection-editor-groups">
            {sources.map((source, index) => {
              const isExpanded = expanded === source.id;
              const searching = source.query != null;
              const groupName = t('merchandising.advancedPrototype.group', {
                number: index + 1,
              });
              const selectedFields = [
                ...new Set(
                  conditionLeaves(source.conditions)
                    .map(item =>
                      support.fields.find(field => field.key === item.field)
                    )
                    .filter(Boolean)
                    .map(labelFor)
                ),
              ];
              return (
                <React.Fragment key={source.id}>
                  {index > 0 && (
                    <div className="mp-selection-plus" aria-hidden="true">
                      <AddIcon />
                    </div>
                  )}
                  <section
                    className={`mp-selection-source ${
                      isExpanded ? 'is-expanded' : ''
                    }`}
                    data-selection-source={source.id}
                    aria-label={groupName}
                  >
                    <header>
                      <button
                        type="button"
                        className="mp-selection-source-toggle"
                        aria-label={t('merchandising.advanced.editGroup', {
                          number: index + 1,
                        })}
                        aria-expanded={isExpanded}
                        aria-controls={`selection-body-${source.id}`}
                        onClick={() => {
                          pendingReveal.current = isExpanded ? null : source.id;
                          setEnteredGroup(null);
                          setExpanded(isExpanded ? null : source.id);
                        }}
                      >
                        <span className="mp-selection-source-number">
                          {index + 1}
                        </span>
                        <span className="mp-selection-source-summary">
                          <strong>{groupName}</strong>
                          <span>
                            {source.query?.trim() ||
                              t(
                                searching
                                  ? 'merchandising.advanced.searchResults'
                                  : 'merchandising.advanced.allCatalog'
                              )}
                            {selectedFields.length
                              ? ` · ${selectedFields.join(', ')}`
                              : ''}
                          </span>
                        </span>
                        {support.selectionSourcePreview && (
                          <SelectionSourceCount preview={previews[source.id]} />
                        )}
                        <ExpandMoreIcon />
                      </button>
                      <IconButton
                        aria-label={t(
                          'merchandising.advancedPrototype.groupRemove',
                          { number: index + 1 }
                        )}
                        onClick={() => {
                          const remaining = sources.filter(
                            item => item.id !== source.id
                          );
                          setDraft({ ...draft, sources: remaining });
                          if (isExpanded) {
                            const nextId =
                              remaining[Math.min(index, remaining.length - 1)]
                                ?.id || null;
                            pendingReveal.current = nextId;
                            setEnteredGroup(null);
                            setExpanded(nextId);
                          }
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </header>
                    <Collapse
                      appear
                      in={isExpanded}
                      onEntered={() => setEnteredGroup(source.id)}
                      timeout={reduceMotion ? 0 : 180}
                      unmountOnExit
                      aria-hidden={!isExpanded}
                      inert={isExpanded ? undefined : ''}
                    >
                      <div
                        className="mp-selection-source-body"
                        id={`selection-body-${source.id}`}
                      >
                        <div className="mp-selection-source-origin">
                          <strong>
                            {t('merchandising.advanced.productsFrom')}
                          </strong>
                          <SegmentedControl
                            value={searching ? 'search' : 'catalog'}
                            label={t('merchandising.advanced.productsFrom')}
                            options={[
                              {
                                VALUE: 'catalog',
                                LABEL: t('merchandising.advanced.allCatalog'),
                              },
                              {
                                VALUE: 'search',
                                LABEL: t(
                                  'merchandising.advanced.searchResults'
                                ),
                              },
                            ]}
                            onChange={mode => {
                              if ((mode === 'search') === searching) return;
                              updateSource(source.id, current => ({
                                ...current,
                                query: mode === 'search' ? '' : null,
                              }));
                            }}
                          />
                          {searching && (
                            <label className="mp-advanced-field mp-selection-search-field">
                              <span>
                                {t('merchandising.advanced.searchQuery')}
                              </span>
                              <div>
                                <SearchIcon />
                                <input
                                  maxLength={500}
                                  value={source.query}
                                  onChange={event => {
                                    const query = event.target.value;
                                    updateSource(source.id, current => ({
                                      ...current,
                                      query,
                                    }));
                                  }}
                                />
                              </div>
                            </label>
                          )}
                        </div>
                        <div className="mp-selection-refinements">
                          <strong>
                            {t('merchandising.advanced.refineProducts')}
                          </strong>
                          {!source.conditions.items.length && (
                            <p>
                              {t(
                                searching
                                  ? 'merchandising.advanced.searchConditionsOptional'
                                  : 'merchandising.advanced.chooseConditions'
                              )}
                            </p>
                          )}
                          <ConditionBuilder
                            conditions={source.conditions}
                            schema={conditionSchema}
                            productApi={productApi}
                            languageId={languageId}
                            advancedConditions={support.richConditions}
                            allowGroups={support.richConditions}
                            onChange={conditions =>
                              updateSource(source.id, current => ({
                                ...current,
                                conditions,
                              }))
                            }
                            renderExtra={(condition, onUpdate) =>
                              condition.field === 'category_ids' ? (
                                <label className="mp-checkbox-label mp-selection-category-scope">
                                  <SelectionCheckbox
                                    checked={
                                      condition.include_descendants !== false
                                    }
                                    label={t(
                                      'merchandising.advanced.descendants'
                                    )}
                                    onChange={event =>
                                      onUpdate({
                                        include_descendants:
                                          event.target.checked,
                                      })
                                    }
                                  />
                                  <span>
                                    {t('merchandising.advanced.descendants')}
                                  </span>
                                </label>
                              ) : null
                            }
                          />
                          <Button
                            className="mp-selection-add-condition"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            disabled={
                              conditionLeaves(source.conditions).length >= 10 ||
                              !support.fields.length
                            }
                            onClick={() => {
                              setFilterQuery('');
                              setConditionMenu({ sourceId: source.id });
                            }}
                          >
                            {t('merchandising.advanced.addCondition')}
                          </Button>
                        </div>
                        {support.selectionSourcePreview && (
                          <SelectionSourcePreview
                            groupName={groupName}
                            preview={previews[source.id]}
                            onRetry={() => retry(source.id)}
                          />
                        )}
                      </div>
                    </Collapse>
                  </section>
                </React.Fragment>
              );
            })}
          </div>
          <p
            className="mp-selection-draft-hint"
            role={!draftValid ? 'status' : undefined}
            aria-hidden={draftValid}
          >
            {t('merchandising.selectionPreview.saveIncomplete')}
          </p>
        </DialogContent>
        <DialogActions>
          <Button
            className="mp-selection-add-group"
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={sources.length >= 5}
            onClick={addGroup}
          >
            {t('merchandising.advanced.addProductGroup')}
          </Button>
          <div className="mp-selection-save-actions">
            <Button variant="outlined" onClick={close}>
              {t('merchandising.advancedPrototype.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!draftValid}
              onClick={() =>
                onSave({
                  ...canonicalPolicy(draft),
                  sources: canonicalPolicy(draft).sources.map(source => ({
                    ...source,
                    query: source.query?.trim() || null,
                  })),
                })
              }
            >
              {t('merchandising.advancedPrototype.saveSelection')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <ConditionFieldPicker
        open={Boolean(conditionMenu)}
        query={filterQuery}
        onQueryChange={setFilterQuery}
        options={fieldOptions}
        onClose={() => setConditionMenu(null)}
        onPick={({ field, descriptor }) => {
          if (!conditionMenu) return;
          const operator = preferredOperator(descriptor);
          updateSource(conditionMenu.sourceId, source => ({
            ...source,
            conditions: appendCondition(source.conditions, {
              field,
              operator,
              value: defaultConditionValue(descriptor, operator),
              ...(field === 'category_ids'
                ? { include_descendants: true }
                : {}),
            }),
          }));
          setConditionMenu(null);
        }}
      />
      <UnsavedChangesDialog
        message={t('merchandising.advanced.discardSelection')}
        guard={{
          open: discard,
          keepEditing: () => setDiscard(false),
          discardAndLeave: () => {
            setDiscard(false);
            onClose();
          },
        }}
      />
    </>
  );
}
